import type { DefinedError, ErrorObject } from 'ajv'
import type * as Ajv from 'ajv'
import type {
	FieldError,
	FieldValues,
	ResolverOptions,
	ResolverResult,
} from 'react-hook-form'

import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers'
import ajvErrors from 'ajv-errors'
import ajvFormats from 'ajv-formats'
import Ajv2020 from 'ajv/dist/2020'
import { appendErrors } from 'react-hook-form'

export type Resolver = <T>(
	schema: Ajv.JSONSchemaType<T>,
	schemaOptions?: Ajv.Options,
	factoryOptions?: { mode?: 'async' | 'sync' }
) => <TFieldValues extends FieldValues, TContext>(
	values: TFieldValues,
	context: TContext | undefined,
	options: ResolverOptions<TFieldValues>
) => Promise<ResolverResult<TFieldValues>>

// ajv doesn't export any types for errors with `keyword='errorMessage'`
type ErrorMessage = ErrorObject<
	'errorMessage',
	{ errors: (DefinedError & { emUsed: boolean })[] }
>
export type AjvError = ErrorMessage | DefinedError
const parseErrorSchema = (
	ajvErrors: AjvError[],
	validateAllFieldCriteria: boolean
) => {
	const parsedErrors: Record<string, FieldError> = {}

	const reduceError = (error: AjvError) => {
		// Ajv will return empty instancePath when require error
		if (error.keyword === 'required') {
			error.instancePath += `/${error.params.missingProperty}`
		}

		// `/deepObject/data` -> `deepObject.data`
		const path = error.instancePath.substring(1).replace(/\//g, '.')

		if (!parsedErrors[path]) {
			parsedErrors[path] = {
				message: error.message,
				type: error.keyword,
			}
		}

		if (validateAllFieldCriteria) {
			const types = parsedErrors[path].types
			const messages = types && types[error.keyword]

			parsedErrors[path] = appendErrors(
				path,
				validateAllFieldCriteria,
				parsedErrors,
				error.keyword,
				messages
					? ([] as string[]).concat(messages as string[], error.message || '')
					: error.message
			) as FieldError
		}
	}

	for (let index = 0; index < ajvErrors.length; index += 1) {
		const error = ajvErrors[index]

		if (error.keyword === 'errorMessage') {
			error.params.errors.forEach((originalError) => {
				originalError.message = error.message
				reduceError(originalError)
			})
		} else {
			reduceError(error)
		}
	}

	return parsedErrors
}

/**
 * Creates a resolver for react-hook-form using Ajv schema validation
 * @param {Schema} schema - The Ajv schema to validate against
 * @param {Object} schemaOptions - Additional schema validation options
 * @param {Object} resolverOptions - Additional resolver configuration
 * @param {string} [resolverOptions.mode='async'] - Validation mode
 * @returns {Resolver<Schema>} A resolver function compatible with react-hook-form
 * @example
 * const schema = ajv.compile({
 *   type: 'object',
 *   properties: {
 *     name: { type: 'string' },
 *     age: { type: 'number' }
 *   }
 * });
 *
 * useForm({
 *   resolver: ajvResolver(schema)
 * });
 */
export const ajvResolver: Resolver =
	(schema, schemaOptions, resolverOptions = {}) =>
	async (values, _, options) => {
		const ajv = new Ajv2020(
			Object.assign(
				{},
				{
					allErrors: true,
					validateSchema: true,
				},
				schemaOptions
			)
		)

		ajvErrors(ajv)
		ajvFormats(ajv)

		const validate = ajv.compile(
			Object.assign(
				{ $async: resolverOptions && resolverOptions.mode === 'async' },
				schema
			)
		)

		const valid = validate(values)

		options.shouldUseNativeValidation && validateFieldsNatively({}, options)

		return valid
			? { values, errors: {} }
			: {
					values: {},
					errors: toNestErrors(
						parseErrorSchema(
							validate.errors as DefinedError[],
							!options.shouldUseNativeValidation &&
								options.criteriaMode === 'all'
						),
						options
					),
				}
	}
